import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { sortBy } from 'lodash';
import {
  withStyles,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Grid,
  Avatar,
  Icon,
  Card,
  CardHeader,
  CardContent,
} from '@material-ui/core';
import { blue } from '@material-ui/core/colors';
import { getStatuses } from '@pingum/app-state-management/status/actions';
import { getTagTypes } from '@pingum/app-state-management/tagType/actions';
import { getEventData } from '@pingum/app-state-management/eventData/actions';
import { getEventTemplates } from '@pingum/app-state-management/eventTemplate/actions';
import {
  textType,
  numberType,
  dateType,
  dateTimeType,
  userType,
} from '@pingum/api-services/EventDataService/EventDataTypes';
import EventTemplateList from '../../EventTemplateSettings/components/EventTemplateList';
import Tag from '../../../widgets/Tag';

export const styles = (theme) => ({
  rootContainer: { flexGrow: 1 },
  list: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
    overflow: 'auto',
    height: 400,
    paddingRight: theme.spacing(4),
  },
  idField: {
    color: blue[600],
    fontWeight: 500,
  },
});

// tag type
export const buildTagTree
  = (level, tags, tagType, classes) => (
    <ul>
      {sortBy(tags, 'name').map((tag) => (
        <li key={tag.id}>
          <Tag
            tag={tag}
            icon={tagType.icon}
            color={tagType.iconColor}
          /> ID: <span className={classes.idField}>{tag.id}</span>
          {tag.children
             && buildTagTree(
               level + 1,
               tag.children,
               tagType,
               classes,
             )}
        </li>
      ))}
    </ul>
  );

const getIconForEventData = (type) => {
  switch (type) {
    case textType:
      return 'text_format';
    case numberType:
      return 'plus_one';
    case dateType:
      return 'date_range';
    case dateTimeType:
      return 'schedule';
    case userType:
      return 'people';
    default:
      return '';
  }
};

const getExampleValues = (type) => {
  if (type === textType) {
    return 'example value: "My Text Value In String Format"';
  }
  if (type === numberType) {
    return 'example value: "12345"';
  }
  if (type === dateType) {
    return 'example value: "2019-10-09T05:00:00.000Z"';
  }
  if (type === dateTimeType) {
    return 'example value: "2019-10-01T13:50:00.000Z"';
  }
  if (type === userType) {
    return 'example value: "myemail@gmail.com,otheruseremail@company.com"';
  }
  return '';
};

export class EntityIDSearchCardComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      pagination: {
        page: 0,
        size: 10,
        search: '',
        sort: ['name,asc'],
      },
    };

    // retrieve data for search
    props.getStatuses();
    props.getTagTypes();
    // props.getTags();
    props.getEventData();
    props.getEventTemplates(
      false,
      this.state.pagination,
    );
  }

  handlePaginationUpdate = (includeArchived, pagination) => {
    this.setState({ pagination }, () => {
      this.props.getEventTemplates(includeArchived, this.state.pagination);
    });
  }

  render() {
    const {
      statuses,
      eventTemplates,
    } = this.props;
    const { pagination } = this.state;

    return (
      <div>
        <Card>
          <CardHeader
            color="primary"
            icon
            subheader="Manage existing or add new users to your organization"
          >
            <h4 className={this.props.classes.cardIconTitle}>
              Search For Status, Tag Type, Tag, Custom Fields, and Workflow IDs
            </h4>
          </CardHeader>
          <CardContent>
            <Typography gutterBottom>
              Use the below forms to seach for the entity IDs you need to invoke the APIs
            </Typography>
          </CardContent>
        </Card>
        <Grid container spacing={3}>
          <Grid item xs md={6}>

            <Typography variant="h5">
              Statuses
            </Typography>
            <Typography style={{ height: 75 }} component="div" variant="caption" gutterBottom>
              When creating or adding a status to an event, you will need to know
              the ID of the status you want to place the event into.
            </Typography>
            <List dense className={this.props.classes.list} subheader={<li />}>
              {this.props.statuses.sort((a, b) => a.order - b.order).map((status) => (
                <ListItem key={`status-${status.id}`} divider>
                  <ListItemAvatar>
                    <Avatar style={{ color: status.iconColor }}>
                      <Icon>{status.icon}</Icon>
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={status.name} secondary={status.description} />
                  <ListItemSecondaryAction>
                    ID: <span className={this.props.classes.idField}>{status.id}</span>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs md={6}>
            <Typography variant="h5" component="div">
              Custom Fields <small>(also known as Event Data)</small>
            </Typography>
            <Typography style={{ height: 75 }} component="div" variant="caption" gutterBottom>
              From the Pingum system, a custom field is known as Event Data. When providing
              or updating custom field data on an event, look for the Event Data fields in the
              request body of the API calls. All values are passed in as strings.
            </Typography>
            <List dense className={this.props.classes.list} subheader={<li />}>
              {
                this.props.eventData
                  .sort((a, b) => a.name - b.name)
                  .map((data) => (
                    <ListItem key={`eventdata-${data.id}`} divider>
                      <ListItemAvatar>
                        <Avatar>
                          <Icon>{getIconForEventData(data.type)}</Icon>
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        secondaryTypographyProps={{ component: 'div' }}
                        primary={data.name}
                        secondary={(
                          <div style={{ marginLeft: 16 }}>
                            <div> {`"${data.name}": {`} </div>
                            <div style={{ marginLeft: 16 }}> {`"type": "${data.type}",`} </div>
                            <div style={{ marginLeft: 16 }}> {'"value": "<value here>"'} </div>
                            <div>&#125;</div>
                            <div>{getExampleValues(data.type)}</div>
                          </div>
                        )}
                      />
                    </ListItem>
                  ))
              }
            </List>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs md={12}>
            <Typography variant="h5">
              Tags
            </Typography>
            <Typography style={{ height: 25 }} component="div" variant="caption" gutterBottom>
              When creating or updating an event, you can pass in a list of Tag IDs
              that you wish to associate with a given event.
            </Typography>
            <List dense className={this.props.classes.list} subheader={<li />}>
              {
                this.props.tagTypes
                  .map((tagType) => (
                    <div key={`tagtype-${tagType.id}`}>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar style={{ color: tagType.iconColor }}>
                            <Icon>{tagType.icon}</Icon>
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText primary={tagType.name} secondary={tagType.description} />
                        <ListItemSecondaryAction>
                          ID: <span className={this.props.classes.idField}>{tagType.id}</span>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <ListItem divider className={classNames('tags')} component="div">
                        {
                          buildTagTree(
                            0,
                            tagType.tags,
                            tagType,
                            this.props.classes,
                          )
                        }
                      </ListItem>
                    </div>
                  ))
              }
            </List>
          </Grid>
        </Grid>
        <Typography variant="h5" className={this.props.classes.title}>
          Workflows
        </Typography>

        <EventTemplateList
          onPaginationUpdate={this.handlePaginationUpdate}
          eventTemplates={eventTemplates}
          pagination={pagination}
          readOnly
          statuses={statuses}
          showIdColumn
        />
      </div>
    );
  }
}

EntityIDSearchCardComponent.propTypes = {
  classes: PropTypes.object,
  getStatuses: PropTypes.func,
  statuses: PropTypes.array,
  getTagTypes: PropTypes.func,
  tagTypes: PropTypes.array,
  getEventData: PropTypes.func,
  eventData: PropTypes.array,
  getEventTemplates: PropTypes.func,
  eventTemplates: PropTypes.object,
};

EntityIDSearchCardComponent.defaultProps = {
  classes: {},
  getStatuses: () => {},
  statuses: [],
  getTagTypes: () => {},
  tagTypes: [],
  getEventData: () => {},
  eventData: [],
  getEventTemplates: () => {},
  eventTemplates: { content: [] },
};

export const mapStateToProps = (state) => ({
  statuses: state.status.statuses,
  tagTypes: state.tagType.tagTypes,
  eventData: state.eventData.eventData,
  eventTemplates: state.eventTemplate.eventTemplatesPageable,
});

export const mapDispatchToProps = (dispatch) => ({
  getStatuses: () => dispatch(getStatuses(false)),
  getTagTypes: () => dispatch(getTagTypes(false, true)),
  getEventData: () => dispatch(getEventData(false)),
  getEventTemplates: (includeArchived, pagination) => dispatch(getEventTemplates(includeArchived, pagination)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(EntityIDSearchCardComponent));
